import React from 'react';
import { graphql, Link } from 'gatsby';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import styled from 'styled-components';

import Layout from '../components/PageLayout';
import Seo from '../components/seo';
import RelatedArticles from '../components/RelatedArticles';
import { Container, Title, ArticleBody, Header } from './post-styles';

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ImageWrapper = styled.img`
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  display: block;
`;

const Badge = styled(Link)`
  background: #e8e8e8;
  color: #444;
  text-decoration: none;
  border-radius: 4px;
  display: inline-block;
  font-size: 11px;
  font-family: sans-serif;
  font-weight: 600;
  padding: 2px 5px;
  text-shadow: none;
  vertical-align: middle;
  white-space: nowrap;
`;

const StandFirst = styled.p`
  font-size: 2rem;
  line-height: 1.5;
  font-weight: 600;
  font-family: var(--body-font-family);
`;

const processMarkdown = markdown => {
  return markdown
    .replace(/\\'/g, "'") // \' replaced by '
    .replace(/\\"/g, '"') // \" replaced by "
    .replace(/\\\./g, '.') // \. replaced by .
    .replace(/#([^\s#])/g, '# $1') // Add space after single hash
    .replace(/##([^\s#])/g, '## $1') // Add space after double hash
    .replace(/###([^\s#])/g, '### $1') // Add space after triple hash
    .replace(/####([^\s#])/g, '#### $1') // Add space after quadruple hash
    .replace(/#####([^\s#])/g, '##### $1'); // Add space after quintuple hash
};

const getMiddleHeadingIndex = paragraphs => {
  const headingIndices = paragraphs
    .map((p, i) => (p.trim().startsWith('#') ? i : -1))
    .filter(i => i !== -1);

  return headingIndices.length > 0
    ? headingIndices[Math.floor(headingIndices.length / 2)]
    : null;
};

const calculateSplitIndex = processedBody => {
  const paragraphs = processedBody.split('\n\n');
  const middleHeadingIndex = getMiddleHeadingIndex(paragraphs);

  return middleHeadingIndex !== null
    ? paragraphs.slice(0, middleHeadingIndex).join('\n\n').length
    : null;
};

const Article = props => {
  const { data } = props;
  const { strapiArticle: article } = data;
  const {
    author = {},
    image = {},
    read_next_articles = [],
    related_articles = [],
  } = article;

  const processedBody = processMarkdown(article.body);
  const splitIndex = calculateSplitIndex(processedBody);
  const firstSplit = processedBody.substring(0, splitIndex);
  const secondSplit = processedBody.substring(splitIndex);

  return (
    <Layout>
      <Seo
        title={article?.title_tag || article?.title}
        description={article?.meta_description || article?.stand_first}
      />
      <Container>
        <Header>
          <Title>{article?.title}</Title>
          {!!(author && author.name) && <p>By {author.name}</p>}
          <Badges>
            {article.categories.map(({ name, slug }) => {
              return (
                <Badge key={slug} to={`/articles/categories/${slug}`}>
                  {name}
                </Badge>
              );
            })}
          </Badges>
        </Header>
        <StandFirst>{article.stand_first}</StandFirst>
        {image && image.formats.small.url && (
          <ImageWrapper src={image.formats.small.url} alt={article.title} />
        )}

        <ArticleBody>
          <Markdown rehypePlugins={[rehypeRaw]}>{firstSplit}</Markdown>{' '}
        </ArticleBody>

        <RelatedArticles articles={related_articles} limit={6} type="sidebar" />
        <ArticleBody>
          <Markdown rehypePlugins={[rehypeRaw]}>{secondSplit}</Markdown>
        </ArticleBody>

        <RelatedArticles
          articles={read_next_articles}
          articleId={article.id}
          type="readnext"
        />
      </Container>
    </Layout>
  );
};

export const query = graphql`
  query StrapiArticleTemplateQuery($id: String!) {
    strapiArticle(id: { eq: $id }) {
      id
      title
      title_tag
      meta_description
      stand_first
      body
      slug
      read_next_articles {
        id
        title
        slug
        stand_first
        image {
          formats {
            small {
              url
            }
          }
        }
      }
      related_articles {
        id
        title
        slug
        stand_first
        image {
          formats {
            small {
              url
            }
          }
        }
      }
      image {
        formats {
          small {
            url
          }
        }
      }
      categories {
        id
        name
        slug
      }
      author {
        id
        name
      }
    }
  }
`;

export default Article;
